import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { useAuth } from './useAuth';
import type { NewsApiSettings } from '../types';

export function useNewsApiSettings() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery<NewsApiSettings[]>({
    queryKey: ['news_api_settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('news_api_settings')
        .select('id, provider, enabled, config, updated_at')
        .order('provider');
      if (error) throw error;
      return (data ?? []) as NewsApiSettings[];
    },
    enabled: !!isAdmin,
    staleTime: 60000,
  });

  const upsertMutation = useMutation({
    mutationFn: async (row: { provider: string; enabled: boolean; api_key_encrypted?: string | null; config?: Record<string, unknown> }) => {
      const { data, error } = await supabase
        .from('news_api_settings')
        .upsert(
          {
            provider: row.provider,
            enabled: row.enabled,
            api_key_encrypted: row.api_key_encrypted ?? null,
            config: row.config ?? {},
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'provider' }
        )
        .select()
        .single();
      if (error) throw error;
      return data as NewsApiSettings;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news_api_settings'] });
    },
  });

  return {
    settings: query.data ?? [],
    loading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    upsertSetting: upsertMutation.mutateAsync,
  };
}
